import { Injectable, Inject } from '@angular/core';
import {
  Constants,
  ConstantsDefinition,
  ConstantsElementType,
  ConstantsFunctionality,
  ConstantsKnowledgeLevel,
  ConstantsProjectPveFase,
  ConstantsProjectRole,
  ConstantsProjectLevel,
  ConstantsQualityLevel,
  ConstantsRequirementType,
  ConstantsRequirementStrictness,
  ConstantsStatusType,
  ConstantsUnitType,
  ConstantsUserLanguage,
  ConstantsUserRole,
  ConstantsUserStatus
} from './constants.interface';
import { BehaviorSubject } from 'rxjs';
import { orderBy } from 'lodash-es';

/**
 * Constants service.
 */
@Injectable({
  providedIn: 'root'
})
export class ConstantsService {

  /** Constants. */
  private CONSTANTS: Constants;

  /** Sidebar state Subject. */
  private _constants = new BehaviorSubject<boolean>(false);

  /** Sidebar state Observable */
  public constant$ = this._constants.asObservable();

  /** Legacy constants service hook. */
  private sgConstantsService: any;

  /** Legacy UI router $transitions hook. */
  private $sgTransitions: any;

  /**
   * Constructor.
   *
   * @todo Currently uses ui-router $transitions hook for route change detection, since the
   *  Angular router isn't configured yet. Switch to Router events after migration.
   **/
  constructor(
    @Inject('sgConstants') sgConstantsService: any,
    @Inject('$sgTransitions') $sgTransitions: any
  ) {
    this.sgConstantsService = sgConstantsService;
    this.$sgTransitions = $sgTransitions;
    this.CONSTANTS = this.sgConstantsService.getAll();

    this.$sgTransitions.onSuccess({}, (res) => {
      this.CONSTANTS = this.sgConstantsService.getAll();
      this._constants.next(this.sgConstantsService.getAll());
    });
  }

  /**
   * Get the whole constants object.
   *
   * @returns All constants.
   */
  public get(): Constants {
    return this.CONSTANTS;
  }

  /**
   * Get comment levels constants.
   *
   * @todo Create specific interface.
   *
   * @returns Comment levels constants.
   */
  public getCommentLevels(): Constants['COMMENT_LEVELS'] {
    return this.CONSTANTS.COMMENT_LEVELS;
  }

  /**
   * Get definition constants.
   *
   * @param sort Sorting key, defaults to `key`.
   * @returns Definition constants.
   */
  public getDefinitions(sort: keyof ConstantsDefinition = 'key'): ConstantsDefinition[] {
    return orderBy(this.CONSTANTS.DEFINITIONS, sort);
  }

  /**
   * Get element type constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Element type constants.
   */
  public getElementTypes(sort: keyof ConstantsElementType = '_id'): ConstantsElementType[] {
    return orderBy(this.CONSTANTS.ELEMENT_TYPES, sort);
  }

  /**
   * Get functionality constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Functionality constants.
   */
  public getFunctionalitites(sort: keyof ConstantsFunctionality = '_id'): ConstantsFunctionality[] {
    return orderBy(this.CONSTANTS.FUNCTIONALITIES, sort);
  }

  /**
   * Get knowledge levels constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Knowledge levels constants.
   */
  public getKnowledgeLevels(sort: keyof ConstantsKnowledgeLevel = '_id'): ConstantsKnowledgeLevel[] {
    return orderBy(this.CONSTANTS.KNOWLEDGE_LEVELS, sort);
  }

  /**
   * Get project levels constants.
   *
   * @todo Currently globally removes 'Cluster' as a project level by deleting it from the constants
   *  Maybe we should delete it in the database? Note that the id's of the items *after* cluster should
   *  retain their ids! Otherwise a lot of checks in the app fail.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Project levels constants.
   */
  public getProjectLevels(sort: keyof ConstantsProjectLevel = '_id'): ConstantsProjectLevel[] {
    return orderBy(this.CONSTANTS.PROJECT_LEVELS.filter(projectLevel => projectLevel._id !== 3), sort);
  }

  /**
   * Get project role constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Project role constants.
   */
  public getProjectRoles(sort: keyof ConstantsProjectRole = '_id'): ConstantsProjectRole[] {
    return orderBy(this.CONSTANTS.PROJECT_ROLES, sort);
  }

  /**
   * Get PvE fase constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns PvE fase constants.
   */
  public getPveFases(sort: keyof ConstantsProjectPveFase = '_id'): ConstantsProjectPveFase[] {
    return orderBy(this.CONSTANTS.PVE_FASES, sort);
  }

  /**
   * Get quality level constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Quality level constants.
   */
  public getQualityLevels(sort: keyof ConstantsQualityLevel = '_id'): ConstantsQualityLevel[] {
    return orderBy(this.CONSTANTS.QUALITY_LEVELS, sort);
  }

  /**
   * Get requirement strictness constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Requirement strictness constants.
   */
  public getRequirementStrictness(sort: keyof ConstantsRequirementStrictness = '_id'): ConstantsRequirementStrictness[] {
    return orderBy(this.CONSTANTS.REQUIREMENT_STRICTNESS, sort);
  }

  /**
   * Get requirement type constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Requirement type constants.
   */
  public getRequirementTypes(sort: keyof ConstantsRequirementType = '_id'): ConstantsRequirementType[] {
    return orderBy(this.CONSTANTS.REQUIREMENT_TYPES, sort);
  }

  /**
   * Get status type constant.
   *
   * @param id Requested id.
   * @returns Status type constants.
   */
  public getStatusType(id: ConstantsStatusType['_id']): ConstantsStatusType {
    return this.CONSTANTS.STATUS_TYPES.find(status => status._id === id);
  }

  /**
   * Get status types constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Status type constants.
   */
  public getStatusTypes(sort: keyof ConstantsStatusType = '_id'): ConstantsStatusType[] {
    return orderBy(this.CONSTANTS.STATUS_TYPES, sort);
  }

  /**
   * Get unit type constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns Unit type constants.
   */
  public getUnitTypes(sort: keyof ConstantsUnitType = '_id'): ConstantsUnitType[] {
    return orderBy(this.CONSTANTS.UNIT_TYPES, sort);
  }

  /**
   * Get user language constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns User language constants.
   */
  public getUserLanguages(sort: keyof ConstantsUserLanguage = '_id'): ConstantsUserLanguage[] {
    return orderBy(this.CONSTANTS.USER_LANGUAGES, sort);
  }

  /**
   * Get user role constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns User role constants.
   */
  public getUserRoles(sort: keyof ConstantsUserRole = '_id'): ConstantsUserRole[] {
    return orderBy(this.CONSTANTS.USER_ROLES, sort);
  }

  /**
   * Get user status constants.
   *
   * @param sort Sorting key, defaults to `_id`.
   * @returns User status constants.
   */
  public getUserStatus(sort: keyof ConstantsUserStatus = '_id'): ConstantsUserStatus[] {
    return orderBy(this.CONSTANTS.USER_STATUS, sort);
  }
}
